import { Box, Button, Flex, IconButton, Progress } from '@hover/blueprint';
import type { IconProp } from '@hover/icons/dist/declarations/src';
import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';

import { useDetectUserSessionQuery } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { Route as Routes } from 'src/shared/route';
import { logError } from 'src/shared/telemetry/logger';

import { Component as IconBack2 } from 'assets/icons/ui/back2.svg';

const stepsOrdered = [
  '/onboarding/signup/email',
  '/onboarding/signup/password',
  '/onboarding/name',
  '/onboarding/persona',
  '/onboarding/type',
  '/onboarding/company',
  '/onboarding/description',
  '/onboarding/usage',
  '/onboarding/invite',
  '/onboarding/phone',
  '/onboarding/download',
];

const mobileHeaderButtonMap: Record<string, 'help' | 'skip' | null> = {
  '/onboarding/signup/email': null,
  '/onboarding/signup/password': null,
  '/onboarding/name': null,
  '/onboarding/persona': 'help',
  '/onboarding/type': 'help',
  '/onboarding/company': null,
  '/onboarding/description': 'skip',
  '/onboarding/usage': 'skip',
  '/onboarding/invite': 'skip',
  '/onboarding/phone': null,
  '/onboarding/download': null,
};

export const ProgressWrapper: React.FC = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const history = useHistory();
  const { pathname } = useLocation();
  // seems to be a bug with the useLazyQuery + network-only fetch policy and the response returning an error
  const { refetch: detectUserSession } = useDetectUserSessionQuery({
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: true,
  });
  // TODO: change this route to the route that should be used when exiting onboarding flow.
  const exitRoute = 'onboarding/name';

  const currentPathIndex = stepsOrdered.indexOf(pathname);

  const unauthenticatedRoutes = [
    '/onboarding/signup/email',
    '/onboarding/signup/password',
  ];
  const isSignUpRoute = unauthenticatedRoutes.includes(pathname);
  const isAuthenticatedRoute = currentPathIndex >= 0 && !isSignUpRoute;

  const nextPath =
    currentPathIndex < stepsOrdered.length - 1
      ? stepsOrdered[currentPathIndex + 1]
      : exitRoute;

  const getProgress = () =>
    ((currentPathIndex + 1) / stepsOrdered.length) * 100;

  const onBack = () => history.goBack();

  const checkUserSession = async () => {
    try {
      const response = await detectUserSession();
      if (!response?.data && isAuthenticatedRoute) {
        history.push(`${Routes.SIGN_UP_ONBOARDING.path}/welcome`);
      }
    } catch (error) {
      logError(error as Error);
      throw error;
    }
  };

  useEffect(() => {
    void checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  if (!isAuthenticatedRoute && !isSignUpRoute) return null;

  return (
    <Box
      alignItems="center"
      flexDirection="column"
      gap={300}
      height={1}
      padding={500}
      width="100%"
    >
      {/* mobile header */}
      <Flex
        display={{ base: 'flex', tablet: 'none' }}
        flexDirection="row"
        justifyContent="space-between"
        marginBottom={500}
        w={1}
      >
        <IconButton
          icon={IconBack2 as IconProp}
          iconSize="tiny"
          label={t('back')}
          onClick={onBack}
          size="large"
          variant="ghost"
        />
        {mobileHeaderButtonMap[pathname] === 'help' && (
          <Button
            as="a"
            href="https://help.hover.to/"
            target="_blank"
            textDecor="none"
            variant="secondary"
          >
            {t('onboarding.help')}
          </Button>
        )}
        {mobileHeaderButtonMap[pathname] === 'skip' && (
          <Button
            onClick={() => {
              history.push(`${nextPath}`);
            }}
            variant="ghost"
          >
            {t('onboarding.skip')}
          </Button>
        )}
      </Flex>
      <Progress
        colorScheme="neutral"
        margin={{ base: 'auto', tablet: 900 }}
        // @ts-ignore
        size="small"
        value={getProgress()}
        width={{ base: '100%', tablet: 800 }}
      />
    </Box>
  );
};
